import
    {
        BaseAdditionalActivity, BaseWorkout, WorkoutTimeFrame, 
        CircuitWorkout, MssWorkout,
    } from '@/types/workouts';
import {VuexModule, Module, Mutation, Action} from 'vuex-class-modules';
import store from '../index';
import runtimeProcessorModule from './runtime-processor.module';
import {CTRL_COMMAND, FeedbackCtrlCmdMessage} from '@/types/socket';
import {ctrlCmdModule} from './control-commands.module';
import {TRAINING_TRIGGERED_BY} from '@/types/active-training';
import workoutService from "@/services/workout.service";

@Module
class EgymTrainingWorkoutActiveModule extends VuexModule
{
    public workoutPartIdx = 0;
    public activeWorkoutTimeFramesArrayIdx = 0;
    public triggeredBy: TRAINING_TRIGGERED_BY | null = null;

    public workout: MssWorkout | null = null;

    get timeFrames(): WorkoutTimeFrame[]
    {
        return this.activeWorkoutTimeFramesArray
            .filter((timeFrame: WorkoutTimeFrame, idx) => idx < this.activeWorkoutTimeFramesArrayIdx);
    }

    get currentTimeMs(): number
    {
        let time = 0;
        if (this.timeFrames.length > 0) {
            time += this.timeFrames.map(timeFrame => timeFrame.duration)
                .reduce((acc, timeFrameDuration) => acc += timeFrameDuration) * 1000;
        }
        const timeLeft = runtimeProcessorModule.totalTime - runtimeProcessorModule.currentTime;
        if (timeLeft < runtimeProcessorModule.totalTime) {
            time += timeLeft;
        }
        return time
    }

    get currentTimeSec(): number
    {
        return this.currentTimeMs / 1000;
    }

    get activeWorkoutPart(): BaseWorkout | BaseAdditionalActivity | null
    {
        return this.workout;
    }

    get activeWorkoutTimeFramesArray(): WorkoutTimeFrame[]
    {
        const timeFrames: WorkoutTimeFrame[] = [];
        if (this.activeWorkoutPart == null) {
            return timeFrames;
        }
        return workoutService.parseEgymBoutiqueTimeFrames(this.activeWorkoutPart as CircuitWorkout);
    }

    get timeFrameCoordinates()
    {
        return {timeFrameIdx: this.activeWorkoutTimeFramesArrayIdx, workoutPartIdx: this.workoutPartIdx};
    }

    get activeTimeFrame(): WorkoutTimeFrame
    {
        return this.activeWorkoutTimeFramesArray[this.activeWorkoutTimeFramesArrayIdx];
    }

    get rounds(): number
    {
        return (this.activeWorkoutPart as CircuitWorkout).roundsData.length;
    }

    get isItACircuit(): boolean
    {
        return this.activeWorkoutTimeFramesArray.filter(tf => tf.duration == -1).length < 1
    }

    get isItRFT(): boolean
    {
        return this.activeWorkoutTimeFramesArray.filter(tf => tf.duration == -1).length > 0
    }

    get activeCircuitRoundIdx(): number
    {
        return this.activeTimeFrame.roundIdx as number;
    }

    get isItTheLastRound(): boolean
    {
        return this.activeCircuitRoundIdx + 1 == this.rounds;
    }

    get activeWorkoutPartName(): string
    {
        return (this.activeWorkoutPart as BaseWorkout)?.name;
    }

    get isItFirstTimeFrame(): boolean
    {
        return this.workoutPartIdx < 1 && this.activeWorkoutTimeFramesArrayIdx < 1;
    }

    get isItTheLastTimeFrame(): boolean
    {
        return this.activeWorkoutTimeFramesArrayIdx >= this.activeWorkoutTimeFramesArray.length - 1;
    }

    get isItFirstTimeFrameInSet(): boolean
    {
        return this.activeWorkoutTimeFramesArrayIdx == 0;
    }

    get isItTheLastTimeFrameInSet(): boolean
    {
        return this.activeWorkoutTimeFramesArrayIdx === this.activeWorkoutTimeFramesArray.length - 1;
    }

    get isItTheMainWorkoutPreparationTime(): boolean
    {
        return (this.isItFirstTimeFrameInSet && !this.activeTimeFrame.restInterval && !this.activeTimeFrame.workInterval);
    }

    get isItPreparationTime(): boolean
    {
        return this.isItFirstTimeFrameInSet && this.activeWorkoutTimeFramesArray.length > 1;
    }

    get totalTime(): number | undefined
    {
        return this.workout?.totalTime;
    }

    get elapsedTime(): number 
    {
        if (this.timeFrames.length > 0) {
            return this.timeFrames.map(timeFrame => timeFrame.duration)
            .reduce((acc, timeFrameDuration) => acc += timeFrameDuration)
        }
        return 0
    }

    @Mutation
    reset()
    {
        this.workoutPartIdx = 0;
        this.activeWorkoutTimeFramesArrayIdx = 0;

        this.workout = null;
        this.triggeredBy = null;
    }

    @Mutation
    setTrriggeredBy(triggeredBy: TRAINING_TRIGGERED_BY)
    {
        this.triggeredBy = triggeredBy;
    }

    @Mutation
    parse(workout: MssWorkout)
    {
        this.workoutPartIdx = 0;
        this.activeWorkoutTimeFramesArrayIdx = 0;
        this.workout = workout as MssWorkout;
    }

    @Mutation
    setTimeFrameIdx(idx: number) { this.activeWorkoutTimeFramesArrayIdx = idx }

    @Mutation
    setWorkoutPartIdx(idx: number) { this.workoutPartIdx = idx }

    @Action
    loadTheNextTimeFrame()
    {
        if (this.activeWorkoutTimeFramesArrayIdx < this.activeWorkoutTimeFramesArray.length - 1) {
            this.setTimeFrameIdx(this.activeWorkoutTimeFramesArrayIdx + 1);
        } else if (!this.isItTheLastTimeFrame) {
            this.setTimeFrameIdx(0);
            this.setWorkoutPartIdx(this.workoutPartIdx + 1);
        }
    }

    @Action
    init(arg: {workout: MssWorkout; triggeredBy: TRAINING_TRIGGERED_BY;})
    {

        this.resetToDefault();

        this.parse(arg.workout);
        this.setTrriggeredBy(arg.triggeredBy);
    }

    @Action
    pause()
    {
        runtimeProcessorModule.pause();

        ctrlCmdModule.triggerCtrlCmd({
            trigger: CTRL_COMMAND.WORKOUT_PAUSE, value: {
                id: this.workout?.id as string,
                time: this.currentTimeSec
            }
        });
    }

    @Action
    resume()
    {
        runtimeProcessorModule.resume();
        ctrlCmdModule.triggerCtrlCmd({
            trigger: CTRL_COMMAND.WORKOUT_RESUME, value: {
                id: this.workout?.id as string,
                time: this.currentTimeSec,
            }
        });
    }

    @Action
    sync(workoutSyncCmd: FeedbackCtrlCmdMessage)
    {
        if (workoutSyncCmd != undefined) {
            const {value} = workoutSyncCmd;
            const {id, timeFrameIdx} = value;
            if (id == this.workout?.id && timeFrameIdx != undefined) {
                this.setTimeFrameIdx(timeFrameIdx);
            }
        }
    }

    @Action
    resetToDefault() { this.reset(); }
}

export default new EgymTrainingWorkoutActiveModule({store, name: 'activeEgymTrainingWorkout'});